import axios, { AxiosResponse } from "axios";
import { useAtom, useSetAtom } from "jotai";
import { environment } from "../../environment";
import { errorCatcher, useSnackbar, useSocket } from "../../hooks";
import { useLogin } from "../login";
import {
    IScanRequest,
    IScanResponse,
    IManualSearch,
    currentScanItemAtom,
    scanLoadingAtom,
    lastScanValueAtom
} from "./types";

type ScanItem = (query: IScanRequest) => Promise<IScanResponse | null>;
type CreateBase64 = (value: string) => string;
type PrintZPL = (zpl: string, print_node_id: number, station_name: string) => void;
type ManualSearchType = (body: IManualSearch) => Promise<IScanResponse | null>;
type ConfirmComplete = (
    orderId: number,
    printer: any,
    forceReconfirm?: boolean
) => Promise<{ success: boolean; error?: string; needsOverride?: boolean }>;
type RemoveBox = (orderId: number, boxType: 'large' | 'small') => Promise<{ success: boolean; data?: any; error?: string }>;
type ReprintBoxLabels = (orderId: number, printer: any) => Promise<{ success: boolean; error?: string }>;

type UseScannerKeyFunctions = {
    scan: ScanItem,
    base64: CreateBase64,
    printZPL: PrintZPL,
    manualSearch: ManualSearchType,
    confirmComplete: ConfirmComplete,
    removeBox: RemoveBox,
    reprintBoxLabels: ReprintBoxLabels
}

type UseScanner = [
    IScanResponse | null,
    UseScannerKeyFunctions
];

export const useScanner = (): UseScanner => {

    const [scan, setScan] = useAtom(currentScanItemAtom);
    const setScanLoading = useSetAtom(scanLoadingAtom);
    const setLastScanValue = useSetAtom(lastScanValueAtom);
    const [employee, authToken, loginFunctions] = useLogin();
    const [, setSnackbar] = useSnackbar();
    const [, socketFunctions] = useSocket();

    const scanItem = async (query: IScanRequest): Promise<IScanResponse | null> => {
        setScanLoading(true);
        setLastScanValue(query.scanValue);
        if (employee !== null) {
            const scanResponse: AxiosResponse<IScanResponse> | void = await axios.post(`${environment.scanUrl}/scan-item`, { ...query, employeeId: employee.id }, {
                headers: {
                    'X-Wallmates-Auth': authToken
                }
            }).catch((err: any) => errorCatcher(err, loginFunctions.logout));
            if (scanResponse) {
                const consumables = [
                    '#box-1#',
                    '#box-2#',
                    '#squeegee#',
                    '#luxpaste#'
                ]
                if (consumables.includes(scanResponse.data.scanValue)) {
                    socketFunctions.sendMessage(JSON.stringify({
                        type: 'scanner',
                        channel: query.channel || 1
                    }));
                }

                if (scanResponse.data && (scanResponse.data as any).message && (scanResponse.data as any).message === 'Waste scanned') {
                    socketFunctions.sendMessage(JSON.stringify({
                        type: 'scanner',
                        channel: query.channel || 1
                    }));
                    return null;
                } else {
                    setScan(scanResponse.data);
                    setScanLoading(false);
                    return scanResponse.data;
                }
            } else {
                setSnackbar({
                    show: true,
                    snackbarLevel: 'error',
                    text: 'There was an error while performing scan'
                })
                setScan(null);
                setScanLoading(false);
                return null;
            }
        } else {
            loginFunctions.logout();
            return null;
        }
    }

    const manualSearch = async (query: IManualSearch): Promise<IScanResponse | null> => {
        setScanLoading(true);
        setLastScanValue(query.query);
        if (employee !== null) {
            const scanResponse: AxiosResponse<IScanResponse> | void = await axios.post(`${environment.scanUrl}/manual-search`, query, {
                headers: {
                    'X-Wallmates-Auth': authToken
                }
            }).catch((err: any) => errorCatcher(err, loginFunctions.logout));
            if (scanResponse) {
                setScan(scanResponse.data);
                setScanLoading(false);
                return scanResponse.data;
            } else {
                setSnackbar({
                    show: true,
                    snackbarLevel: 'error',
                    text: 'There was an error while performing scan'
                })
                setScan(null);
                setScanLoading(false);
                return null;
            }
        } else {
            loginFunctions.logout();
            return null;
        }
    }

    const toBase64 = (value: string) => {
        return btoa(unescape(encodeURIComponent(value)));
    }

    const printZPL = async (zplString: string, printerId: number, stationName?: string) => {
        try {
            await axios.post(`${environment.scanUrl}/force-print`, {
                zpl: zplString,
                printerId,
                stationName,
                contentType: 'zpl',
            }, {
                headers: {
                    'X-Wallmates-Auth': authToken
                }
            });
        } catch (e: any) {
            console.error(e);
            throw new Error('printing error');
        }
    }

    // #region confirmComplete
    const confirmComplete: ConfirmComplete = async (
        orderId: number,
        printer: any,
        forceReconfirm: boolean = false
    ): Promise<{ success: boolean; error?: string; needsOverride?: boolean }> => {
        try {
            await axios.post(`${environment.scanUrl}/confirm-complete`, {
                orderId,
                printer,
                forceReconfirm
            }, {
                headers: {
                    'X-Wallmates-Auth': authToken
                }
            });
            return { success: true };
        } catch (err: any) {
            errorCatcher(err, loginFunctions.logout);
            // 409 means the server refused on purpose: this order already has
            // printed labels, and re-confirming would void them. That needs a
            // deliberate operator decision rather than a silent retry.
            const needsOverride = err?.response?.status === 409 && !forceReconfirm;
            return {
                success: false,
                needsOverride,
                error: err?.response?.data?.message || 'Failed to confirm boxes'
            };
        }
    }

    // #region removeBox
    const removeBox: RemoveBox = async (orderId: number, boxType: 'large' | 'small') => {
        try {
            const response = await axios.delete(`${environment.scanUrl}/box-weight?orderId=${orderId}&boxType=${boxType}`, {
                headers: { 'X-Wallmates-Auth': authToken }
            });
            return { success: true, data: response.data };
        } catch (err: any) {
            errorCatcher(err, loginFunctions.logout);
            return { success: false, error: err?.response?.data?.message || 'Failed to remove box' };
        }
    }

    // #region reprintBoxLabels
    const reprintBoxLabels: ReprintBoxLabels = async (orderId: number, printer: any) => {
        try {
            await axios.post(`${environment.scanUrl}/reprint-box-labels`, { orderId, printer }, {
                headers: { 'X-Wallmates-Auth': authToken }
            });
            return { success: true };
        } catch (err: any) {
            errorCatcher(err, loginFunctions.logout);
            return { success: false, error: err?.response?.data?.message || 'Failed to reprint box labels' };
        }
    }

    const scanKeyFunctions: UseScannerKeyFunctions = {
        scan: scanItem,
        base64: toBase64,
        printZPL,
        manualSearch,
        confirmComplete,
        removeBox,
        reprintBoxLabels
    }

    return [
        scan,
        scanKeyFunctions
    ]

}
